const content = `
# Data Pipeline

The data pipeline downloads and processes building energy profiles from NREL's ComStock dataset, then decomposes them into the four component categories used by the forecasting models.

## ComStock S3 Data

ComStock data is hosted on the **OEDI (Open Energy Data Initiative)** S3 bucket at:

\`\`\`
s3://oedi-data-lake/nrel-pds-building-stock/comstock/athena/2024/comstock_tmy3_release_2/
\`\`\`

Access is anonymous — no AWS credentials required. The dataset includes:

- **Metadata** — Building type, floor area (sqft), vintage, climate zone, HVAC system type, state, county, heating fuel, number of stories
- **Hourly profiles** — 8760-hour timeseries for every end-use category (cooling, heating, fans, pumps, refrigeration, interior equipment, lighting, water systems)

## Component Decomposition

The pipeline maps ComStock end-use columns to four Vortx component categories:

| Component | ComStock Columns Summed |
|-----------|------------------------|
| **HVAC** | cooling + heating + fans + pumps |
| **Refrigeration** | refrigeration |
| **Plug Loads** | interior equipment |
| **Lighting** | interior lighting + exterior lighting |

A quality check verifies that component sums match the total electricity column within 5% tolerance. Buildings exceeding this threshold are flagged.

## Building Types

The pipeline targets nine commercial building types relevant to Vortx deployments:

Warehouse, RefrigeratedWarehouse, RetailStripmall, FullServiceRestaurant, LargeOffice, MediumOffice, SmallOffice, Outpatient, Hospital

## Download Process

From the **Data** tab in Forecasting:

1. Click **Download** for a building type or use **Download All** for the full corpus.
2. The API streams parquet files from S3, extracts metadata and hourly profiles, decomposes into components, and caches results locally.
3. Progress shows building count and download status per type.

## Stratified Sampling

After download, the **sampling** step reduces ~188k buildings to a representative training set of 3,000-6,000 buildings stratified by:

\`\`\`
building_type x climate_zone x peak_demand_quintile x vintage_band
\`\`\`

Within each cell, KMeans clustering on flattened 8760 load profiles selects diverse representatives capturing different peak timing, weekend patterns, and seasonal shapes.

## Coverage Matrix

The Data tab displays a coverage matrix showing how many buildings have been downloaded and sampled per building type and climate zone. Green cells indicate sufficient coverage; amber cells indicate sparse data that may reduce model quality for that combination.
`;

export default content;
